import React, { useEffect, useState } from "react";
import {
  Container,
  Row,
  Col,
  Card,
  CardBody,
  CardTitle,
  Form,
  FormGroup,
  Label,
  Input,
  Button,
} from "reactstrap";
import PageLayout from "../PageLayout";
import { useForm, useController, Controller, set } from "react-hook-form";
import { ErrorMessage } from "@hookform/error-message";
import { toast } from "react-toastify";
import { getPublicationByIdApi, updatePublicationApi } from "src/network/api";
import { useLocation, useNavigate, useParams } from "react-router-dom";
import LoaderComponent from "./LoaderComponent";
const EditPublication = () => {
  const { id } = useParams();
  const location = useLocation(); // Get location data
  const item = location.state?.item; // Access passed state
  const navigate = useNavigate();

  interface Type {
    paper_title: string;
    publication_year: string;
    paper_type: string;
    authors: string;
    issn: string;
    volume: string;
    issue: string;
    live_url: string;
    indexing_with: string[];
    publication_date: string;
    description: string;
  }

  const [pubdata, setPubdata] = useState<Type>({
    paper_title: "",
    publication_year: "",
    paper_type: "",
    authors: "",
    issn: "",
    volume: "",
    issue: "",
    live_url: "",
    indexing_with: [],
    publication_date: "",
    description: "",
  });

  const fetchData = async () => {
    try {
      const response = await getPublicationByIdApi(id);
      if (response.status === 200 && response.data.status === 200) {
        const data = response.data.data;
        const processedData = {
          ...data,
          indexing_with: data.indexing_with
            ? data.indexing_with.split(",").map((item) => item.trim())
            : [],
        };
        setPubdata(processedData);
        setPublication(processedData);
      }
    } catch (e) {
      toast.error(e.response?.data?.message ?? e.message);
    }
  };

  useEffect(() => {
    fetchData();
  }, [id]);

  const [publication, setPublication] = useState({
    paper_title: pubdata?.paper_title,
    publication_year: pubdata?.publication_year,
    paper_type: pubdata?.paper_type,
    authors: pubdata?.authors,
    issn: pubdata?.issn,
    volume: pubdata?.volume,
    issue: pubdata?.issue,
    live_url: pubdata?.live_url ?? "",
    indexing_with: pubdata?.indexing_with ?? "",
    publication_date: pubdata?.publication_date,
    description: pubdata?.description,
  });

  const {
    register,
    handleSubmit,
    reset,
    formState: { errors },
    control,
  } = useForm();

  const [loading, setIsLoading] = useState(false);
  const handleCheckboxChange = (e) => {
    const { value, checked } = e.target;

    setPublication((prevState) => ({
      ...prevState,
      indexing_with: Array.isArray(prevState.indexing_with)
        ? checked
          ? [...prevState.indexing_with, value]
          : prevState.indexing_with.filter((item) => item !== value)
        : checked
        ? [value]
        : [],
    }));
  };

  if (!pubdata.paper_title) {
    return <LoaderComponent />;
  }

  const onSubmit = async (data) => {
    try {
      setIsLoading(true);
      const payload = { ...data, indexing_with: publication.indexing_with };
      let response = await updatePublicationApi(id, payload);
      console.log(payload);
      navigate("/publication-list");
      if (response.status === 200 && response.data.status === 200) {
        toast.success(response.data.message);
      } else {
        toast.error(response.data.message);
      }
    } catch (e) {
      console.error(e);
      toast.error(e.response?.data?.message ?? e.message);
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <PageLayout>
      <Container className="mt-5 mb-5">
        <Row className="justify-content-center">
          <Col md="10" sm="12">
            <Card className="shadow-lg border-0 rounded-lg">
              <CardBody>
                <CardTitle
                  tag="h4"
                  className="text-center text-primary font-weight-bold mb-4"
                >
                  Update Publication
                </CardTitle>
                <Form onSubmit={handleSubmit(onSubmit)}>
                  {/* Title */}
                  <FormGroup>
                    <Label
                      for="paper_title"
                      className="text-dark font-weight-bold"
                    >
                      Title<span className="text-danger">*</span>
                    </Label>
                    <input
                      type="text"
                      placeholder="Enter the title of the paper"
                      defaultValue={publication.paper_title}
                      className={
                        "form-control" +
                        (!errors.paper_title ? "" : " danger-border")
                      }
                      {...register("paper_title", {
                        required: "This field is required",
                        // value: publication.paper_title,
                        // pattern: {
                        //   value: /^[a-zA-Z\s]+$/,
                        //   message: "Invalid value entered ",
                        // },
                      })}
                    />
                    <p className="error-text">
                      <ErrorMessage errors={errors} name="paper_title" />
                    </p>
                  </FormGroup>
                  {/* Authors */}
                  <FormGroup>
                    <Label for="authors" className="text-dark font-weight-bold">
                      Authors<span className="text-danger">*</span>
                    </Label>
                    <input
                      type="text"
                      placeholder="Enter the author of the paper"
                      defaultValue={publication.authors}
                      className={
                        "form-control" +
                        (!errors.authors ? "" : " danger-border")
                      }
                      {...register("authors", {
                        required: "This field is required",
                        // value: publication.authors,
                        pattern: {
                          value: /^[a-zA-Z\s]+$/,
                          message: "Invalid value entered ",
                        },
                      })}
                    />
                    <p className="error-text">
                      <ErrorMessage errors={errors} name="authors" />
                    </p>
                  </FormGroup>
                  {/* Publication Year */}
                  <FormGroup>
                    <Label
                      for="publication_year"
                      className="text-dark font-weight-bold"
                    >
                      Publication Year<span className="text-danger">*</span>
                    </Label>
                    <input
                      type="text"
                      placeholder="Enter the publication year"
                      defaultValue={publication.publication_year}
                      className={
                        "form-control" +
                        (!errors.publication_year ? "" : " danger-border")
                      }
                      {...register("publication_year", {
                        required: "This field is required",
                        // value: publication.publication_year,
                        pattern: {
                          value: /^(19|20)\d{2}$/,

                          message: "Invalid year entered",
                        },
                      })}
                    />
                    <p className="error-text">
                      <ErrorMessage errors={errors} name="publication_year" />
                    </p>
                  </FormGroup>

                  {/* Publication Type */}
                  <FormGroup tag="fieldset">
                    <Label
                      for="paper_type"
                      className="text-dark font-weight-bold"
                    >
                      Publication Type<span className="text-danger">*</span>
                    </Label>
                    <div className="d-flex align-items-center">
                      <FormGroup check className="mr-3">
                        <Label check>
                          <Controller
                            name="paper_type"
                            control={control}
                            defaultValue={pubdata.paper_type} // Set the initial value from API
                            render={({ field }) => (
                              <Input
                                {...field}
                                type="radio"
                                value="Journal"
                                checked={field.value === "Journal"} // Bind the value correctly
                              />
                            )}
                          />
                          Journal
                        </Label>
                      </FormGroup>

                      <FormGroup check className="mr-3">
                        <Label check>
                          <Controller
                            name="paper_type"
                            control={control}
                            defaultValue={pubdata.paper_type} // Set the initial value from API
                            render={({ field }) => (
                              <Input
                                {...field}
                                type="radio"
                                value="Patent"
                                checked={field.value === "Patent"} // Bind the value correctly
                              />
                            )}
                          />
                          Patent
                        </Label>
                      </FormGroup>

                      <FormGroup check>
                        <Label check>
                          <Controller
                            name="paper_type"
                            control={control}
                            defaultValue={pubdata.paper_type} // Set the initial value from API
                            render={({ field }) => (
                              <Input
                                {...field}
                                type="radio"
                                value="Book"
                                checked={field.value === "Book"} // Bind the value correctly
                              />
                            )}
                          />
                          Book
                        </Label>
                      </FormGroup>
                    </div>

                    {/* ISSN/P-ISSN/ISBN */}
                    <FormGroup>
                      <Label
                        for="issn"
                        className="text-dark font-weight-bold mt-3"
                      >
                        ISSN/P-ISSN/ISBN<span className="text-danger">*</span>
                      </Label>
                      <input
                        type="text"
                        placeholder="Enter the ISSN/P-ISSN/ISBN"
                        defaultValue={publication.issn}
                        className={
                          "form-control" +
                          (!errors.issn ? "" : " danger-border")
                        }
                        {...register("issn", {
                          required: "This field is required",
                          // value: publication.issn,
                          pattern: {
                            value: /^[0-9]+$/,
                            message: "Invalid value entered",
                          },
                        })}
                      />
                      <p className="error-text">
                        <ErrorMessage errors={errors} name="issn" />
                      </p>
                    </FormGroup>

                    {/* Indexing With */}
                    <FormGroup>
                      <Label
                        for="indexing_with"
                        className="text-dark font-weight-bold"
                      >
                        Indexing With<span className="text-danger">*</span>
                      </Label>
                      <div className="d-flex flex-wrap align-items-center">
                        {[
                          "SCOPUS",
                          "SSCI",
                          "ESCI",
                          "Thomson Reuters",
                          "SCI-E",
                          "UGC-CARE",
                          "NAAS",
                          "ABCD Index",
                        ].map((indexing) => (
                          <FormGroup check className="mr-3" key={indexing}>
                            <Label check>
                              <Input
                                type="checkbox"
                                name="indexing_with"
                                value={indexing}
                                checked={publication.indexing_with.includes(
                                  indexing
                                )}
                                onChange={handleCheckboxChange}
                              />
                              {indexing}
                            </Label>
                          </FormGroup>
                        ))}
                      </div>
                    </FormGroup>

                    {/* Volume */}

                    <FormGroup>
                      <Label
                        for="volume"
                        className="text-dark font-weight-bold"
                      >
                        Volume<span className="text-danger">*</span>
                      </Label>
                      <input
                        type="text"
                        placeholder="Enter the volume"
                        defaultValue={publication.volume}
                        className={
                          "form-control" +
                          (!errors.volume ? "" : " danger-border")
                        }
                        {...register("volume", {
                          required: "This field is required",
                          // value: publication.volume,
                          pattern: {
                            value: /^[0-9]+$/,
                            message: "Invalid value entered",
                          },
                        })}
                      />
                      <p className="error-text">
                        <ErrorMessage errors={errors} name="volume" />
                      </p>
                    </FormGroup>

                    {/* Issue */}
                    <FormGroup>
                      <Label for="issue" className="text-dark font-weight-bold">
                        Issue<span className="text-danger">*</span>
                      </Label>
                      <input
                        type="text"
                        placeholder="Enter the issue number"
                        defaultValue={publication.issue}
                        className={
                          "form-control" +
                          (!errors.issue ? "" : " danger-border")
                        }
                        {...register("issue", {
                          required: "This field is required",
                          // value: publication.issue,
                          pattern: {
                            value: /^[0-9]+$/,
                            message: "Invalid value entered",
                          },
                        })}
                      />
                      <p className="error-text">
                        <ErrorMessage errors={errors} name="issue" />
                      </p>
                    </FormGroup>

                    {/* Description/Abstract */}
                    <FormGroup>
                      <Label
                        for="description"
                        className="text-dark font-weight-bold"
                      >
                        Description/Abstract
                        <span className="text-danger">*</span>
                      </Label>
                      <textarea
                        placeholder="Enter the description/abstract"
                        defaultValue={publication.description}
                        className={
                          "form-control" +
                          (!errors.description ? "" : " danger-border")
                        }
                        {...register("description", {
                          required: "This field is required",
                          // value: publication.description,
                        })}
                      />
                      <p className="error-text">
                        <ErrorMessage errors={errors} name="description" />
                      </p>
                    </FormGroup>
                    {/* Live Link */}
                    <FormGroup>
                      <Label
                        for="live_url"
                        className="text-dark font-weight-bold"
                      >
                        Live Link<span className="text-danger">*</span>
                      </Label>
                      <input
                        type="text"
                        placeholder="Enter the live link"
                        defaultValue={publication.live_url}
                        className={
                          "form-control" +
                          (!errors.live_url ? "" : " danger-border")
                        }
                        {...register("live_url", {
                          required: "This field is required",
                          // value: publication.live_url,
                          pattern: {
                            value:
                              /^(https?:\/\/)?([\w-]+\.)+[\w-]+(\/[\w-./?%&=]*)?$/,
                            message: "Invalid URL entered",
                          },
                        })}
                      />
                      <p className="error-text">
                        <ErrorMessage errors={errors} name="live_url" />
                      </p>
                    </FormGroup>

                    {/* Date */}
                    <FormGroup>
                      <Label
                        for="publication_date"
                        className="text-dark font-weight-bold"
                      >
                        Publication Date<span className="text-danger">*</span>
                      </Label>
                      <input
                        type="date"
                        defaultValue={publication.publication_date}
                        className={
                          "form-control" +
                          (!errors.publication_date ? "" : " danger-border")
                        }
                        {...register("publication_date", {
                          required: "This field is required",
                          // value: publication.publication_date,
                        })}
                      />
                      <p className="error-text">
                        <ErrorMessage errors={errors} name="publication_date" />
                      </p>
                    </FormGroup>
                  </FormGroup>
                  {/* Submit Button */}
                  <div className="mt-4">
                    {loading ? (
                      <Button
                        color="primary"
                        className="btn-block"
                        type="submit"
                        disabled
                      >
                        Submitting ...
                      </Button>
                    ) : (
                      <Button
                        color="primary"
                        className="btn-block"
                        type="submit"
                      >
                        Update Publication
                      </Button>
                    )}
                  </div>
                </Form>
              </CardBody>
            </Card>
          </Col>
        </Row>
      </Container>
    </PageLayout>
  );
};

export default EditPublication;
